/**
 * Sales-report buckets: the day/hour granularity rule, label formatting and
 * gap filling.
 *
 * `GET /reports/sales` buckets on the **store's** clock and lets Postgres render
 * the label, so a day is `2026-08-13` and an hour is `2026-08-13T14:00` — both
 * store wall-clock readings, neither an instant (TASK-0169; before that the hour
 * label was a UTC instant and the store's 9pm rush was reported at 1am). Two
 * consequences drive everything here:
 *
 *  1. A label is a reading off the clock behind the register, so it is rendered
 *     exactly as it arrived. Nothing in this module applies a UTC offset: `T21:00`
 *     is 9 PM on the chart, in the tooltip and in the table, whether the browser
 *     is in the store, at the owner's house in Arizona, or on a laptop in Berlin.
 *     Re-interpreting it in the browser's zone is the bug this warning exists to
 *     prevent — it would put the same receipt at 9 PM on the transactions screen
 *     and 5 PM on the report. The `Z` and the `timeZone: 'UTC'` below are a
 *     *carrier* for the digits, not a claim that the label is UTC: parse as UTC,
 *     format as UTC, and the offset cancels out of both ends.
 *
 *  2. The API only returns buckets that had sales. A line drawn straight from
 *     16:00 to 18:00 claims the store traded continuously through 17:00, and
 *     the x-axis stops being linear in time. `fillBuckets` restores the missing
 *     buckets as zeroes for the chart; the table and the CSV keep the server's
 *     rows, so what you export is what the server said. It steps in wall-clock
 *     hours, so on the spring-forward day it draws a zero bar for the 2 AM hour
 *     the store never had — one cosmetic bar a year, in exchange for arithmetic
 *     that never needs to know the store's zone.
 */

export type Granularity = 'day' | 'hour';

export const GRANULARITIES = ['day', 'hour'] as const;

/**
 * The longest range that may be shown hourly.
 *
 * Hourly answers an intraday question — when the rush lands, when it is safe
 * to pull the drawer, whether Sunday morning is worth staffing. That question
 * dissolves past a week: a fortnight is ~200 buckets on an axis that carries
 * three labels, and the daily sawtooth swamps the shape inside each day. Seven
 * days also lines up with the range presets, so Today and 7d are hourly and
 * 30d / MTD / YTD are not.
 */
export const HOURLY_MAX_DAYS = 7;

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const HOUR_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:00$/;

const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/** `2026-08-13T14:00` — an hourly bucket, as opposed to a `2026-08-13` day. */
export function isHourLabel(label: string): boolean {
  return HOUR_RE.test(label);
}

function parseDay(value: string): number | null {
  if (!DAY_RE.test(value)) return null;
  const ms = Date.parse(`${value}T00:00:00Z`);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Where a bucket label sits on a nominal timeline, in ms — for stepping,
 * ordering and measuring gaps. **Not an instant.**
 *
 * The label is a store wall-clock reading, and the store's offset is not this
 * browser's business. Reading the digits as if they were UTC gives a number that
 * is wrong by the store's offset and consistently so, which is all the
 * arithmetic here needs: differences, steps and comparisons are unaffected, and
 * `labelAt()` converts back through the same fiction. The one thing it must
 * never be used for is formatting against the browser's zone.
 */
export function bucketOrdinal(label: string): number | null {
  if (isHourLabel(label)) {
    const ms = Date.parse(`${label}:00Z`);
    return Number.isNaN(ms) ? null : ms;
  }
  return parseDay(label);
}

/** Rebuild the server's label from an ordinal — the inverse of
 *  `bucketOrdinal()`, so the same UTC fiction cancels out. */
function labelAt(ms: number, granularity: Granularity): string {
  const iso = new Date(ms).toISOString();
  return granularity === 'hour' ? `${iso.slice(0, 13)}:00` : iso.slice(0, 10);
}

/** Inclusive day count of an ISO date range. 0 when either end is unusable or
 *  the range runs backwards, which callers read as "no opinion". */
export function rangeDays(from: string, to: string): number {
  const a = parseDay(from);
  const b = parseDay(to);
  if (a === null || b === null) return 0;
  const days = Math.floor((b - a) / DAY_MS) + 1;
  return days > 0 ? days : 0;
}

/** Is this range short enough to be worth an hour-by-hour line? */
export function hourlyFits(from: string, to: string): boolean {
  const days = rangeDays(from, to);
  return days > 0 && days <= HOURLY_MAX_DAYS;
}

/**
 * What the screen will actually draw. The operator's choice stays in the URL
 * untouched — the screen collapses to daily and says so, rather than either
 * rendering 400 unreadable buckets or silently rewriting the request.
 */
export function effectiveGranularity(
  requested: Granularity,
  from: string,
  to: string
): Granularity {
  return requested === 'hour' && hourlyFits(from, to) ? 'hour' : 'day';
}

/* ── Label formatting ─────────────────────────────────────────────────────── */

/*
 * `timeZone: 'UTC'` on every formatter is what keeps the label's own digits
 * intact. `bucketOrdinal()` reads them as UTC; formatting them back as UTC undoes
 * that exactly, so `Intl` supplies the wording ("9 PM", "Thu, Aug 13") and never
 * an offset. Drop the option and the browser's zone slides every hour on the
 * chart — four hours for a store operator reading the console from home in
 * Central time, and the intraday rush lands in the wrong shift.
 */
const UTC = 'UTC';
const hourOnly = new Intl.DateTimeFormat('en-US', { hour: 'numeric', timeZone: UTC });
const hourWithDay = new Intl.DateTimeFormat('en-US', {
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  timeZone: UTC,
});
const hourFull = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  timeZone: UTC,
});

/** `2026-08-13T21:00` → `9 PM` (store time). Day labels pass through unchanged. */
export function formatHour(label: string): string {
  const ms = isHourLabel(label) ? bucketOrdinal(label) : null;
  return ms === null ? label : hourOnly.format(ms);
}

/** `2026-08-13T21:00` → `8/13, 9 PM` (store time). */
export function formatHourWithDay(label: string): string {
  const ms = isHourLabel(label) ? bucketOrdinal(label) : null;
  return ms === null ? label : hourWithDay.format(ms);
}

/** `2026-08-13T21:00` → `Thu, Aug 13, 9 PM` (store time) — tables and readouts. */
export function formatBucketFull(label: string): string {
  const ms = isHourLabel(label) ? bucketOrdinal(label) : null;
  return ms === null ? label : hourFull.format(ms);
}

/**
 * The chart axis. Hourly buckets inside one store day need no date; across
 * days they do, or "10 AM" appears three times meaning three different days.
 */
export function formatBucketAxis(label: string, labels: string[]): string {
  if (!isHourLabel(label)) return label;
  return spansOneStoreDay(labels) ? formatHour(label) : formatHourWithDay(label);
}

/**
 * Do every hour label in this set fall on the same *store* day?
 *
 * The label already carries the store's date in its first ten characters, so
 * that is the comparison. Re-deriving it from a Date in the browser's zone split
 * a single store day across two calendar days for any operator west of the
 * store, and the axis grew a redundant `8/13,` on every tick.
 */
export function spansOneStoreDay(labels: string[]): boolean {
  let seen: string | null = null;
  for (const label of labels) {
    if (bucketOrdinal(label) === null) continue;
    const key = label.slice(0, 10);
    if (seen === null) seen = key;
    else if (seen !== key) return false;
  }
  return true;
}

/* ── Gap filling ──────────────────────────────────────────────────────────── */

export interface SalesBucket {
  label: string;
  revenue: number;
  count: number;
  tax: number;
}

/**
 * Restore the buckets the server omitted, as zeroes, so the x-axis is linear
 * in time and a closed hour reads as closed rather than as a straight line
 * between two open ones.
 *
 * Refuses (returning the input untouched) when the labels are unparseable or
 * the filled series would exceed `limit` — a runaway range must not lock the
 * tab up building a million points.
 */
export function fillBuckets(
  rows: SalesBucket[],
  granularity: Granularity,
  limit = 2000
): SalesBucket[] {
  if (rows.length < 2) return rows;
  const step = granularity === 'hour' ? HOUR_MS : DAY_MS;
  const first = bucketOrdinal(rows[0]!.label);
  const last = bucketOrdinal(rows[rows.length - 1]!.label);
  if (first === null || last === null || last < first) return rows;

  const span = Math.round((last - first) / step) + 1;
  if (span <= rows.length || span > limit) return rows;

  const byLabel = new Map(rows.map((r) => [r.label, r]));
  const out: SalesBucket[] = [];
  for (let t = first; t <= last; t += step) {
    const label = labelAt(t, granularity);
    out.push(byLabel.get(label) ?? { label, revenue: 0, count: 0, tax: 0 });
  }
  return out;
}
